import { useTranslation } from "react-i18next"
import { useTeamCoachListQuery, useUpsertCoachMutation } from "@/hooks/api/useCoachQuery"
import { useState, useEffect } from "react"

import BasicButton from "@/components/button/BasicButton"
import TextField from "@/components/form/textField/TextField"
import Selector from "@/components/form/selector/Selector"
import ProfileAvatar from "@/components/profileAvatar/ProfileAvatar"
import { Coach } from "@/types/coach"
import { CoachRole } from "@/constants/coach"
import { createAccountStore } from "@/stores/account"

import "./CoachInfo.scss"
import { createGlobalStore } from "@/stores/global"

export default function CoachInfo({
  selectedId,
  openDialog,
}: {
  selectedId: number
  openDialog: () => void
}) {
  const { t } = useTranslation("")
  const { info } = createAccountStore()
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)
  const [newCoachRole, setNewCoachRole] = useState<CoachRole>(CoachRole.AC)
  const [isSaveDirty, setIsSaveDirty] = useState(false)
  const [selectedCoach, setSelectedCoach] = useState<Coach>()

  const { data } = useTeamCoachListQuery()
  const { mutate: upsertCoach } = useUpsertCoachMutation()
  const roles = Object.values(CoachRole).map((role) => ({ label: t(role), value: role }))

  useEffect(() => {
    setIsSaveDirty(false)
    if (data && selectedId) {
      const newCoach = data.find((item: Coach) => item.idfCoach === selectedId)

      if (!newCoach) {
        return
      }
      setNewCoachRole(newCoach.role as CoachRole)
      setSelectedCoach(newCoach)
    }
  }, [data, selectedId])

  const onChangeField = (value: CoachRole) => {
    setNewCoachRole(value)
    setIsSaveDirty(true)
  }

  if (data === undefined || info === undefined) {
    return null
  }

  const handleSave = () => {
    upsertCoach({
      idfCoach: selectedId || data[selectedId].idfCoach,
      payload: { role: newCoachRole },
    })
    setSnackBar({
      open: true,
      message: t("messages.successUpdate"),
      severity: "success",
    })
    setIsSaveDirty(false)
  }

  return (
    <div className="Coach-Info">
      <div className="Coach-Body">
        <ProfileAvatar imageUrl={selectedCoach?.image} size={72} />
        <div className="Coach-Details">
          <span>{t("name")}</span>
          <TextField
            value={selectedCoach?.name || ""}
            slotProps={{
              input: {
                readOnly: true,
              },
            }}></TextField>
        </div>
        <div className="Coach-Details">
          <span>{t("role")}</span>
          <Selector
            options={roles}
            onChange={(
              _: unknown,
              newRole: {
                label: string
                value: CoachRole
              },
            ) => onChangeField(newRole.value)}
            renderOption={(value) => {
              const { key, ...optionProps } = value

              return (
                <span key={key} {...optionProps}>
                  {t(value.key)}
                </span>
              )
            }}
            value={t(newCoachRole)}
          />
        </div>
        <div className="Coach-Details">
          <span>{t("email")}</span>
          <TextField
            value={selectedCoach?.email || ""}
            slotProps={{
              input: {
                readOnly: true,
              },
            }}></TextField>
        </div>
      </div>
      <div className="Coach-Footer">
        {selectedCoach?.idfCoach === info.idfAccount ? (
          <div></div>
        ) : (
          <BasicButton size="md" type="danger-outlined" onClick={openDialog}>
            {t("button.remove")}
          </BasicButton>
        )}
        <BasicButton
          size="md"
          type="primary-contained"
          disabled={!isSaveDirty}
          onClick={handleSave}>
          {t("button.save")}
        </BasicButton>
      </div>
    </div>
  )
}
